import { useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Truck,
  Phone,
  MapPin,
  Clock,
  Loader2,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  FileText,
  Image as ImageIcon,
  Edit,
  ShoppingCart,
  Trash2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@flote/ui';
import type { DeliveryTaskStatus } from '@flote/types/delivery';
import { toast } from 'sonner';
import { deliveryTsr } from '../../api';
import { TaskEditModal } from '../../components/tasks';
import { TaskStatusBadge, DriverStatusBadge } from '../../components/status-badges';
import { getErrorMessage } from '../../lib/error-utils';

const STATUS_ICONS: Record<DeliveryTaskStatus, typeof CheckCircle> = {
  pending: AlertCircle,
  assigned: User,
  in_transit: Truck,
  arrived: MapPin,
  completed: CheckCircle,
  failed: XCircle,
};

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString();
}

// function formatDate(dateString: string): string {
//   return new Date(dateString).toLocaleDateString();
// }

// function formatTime(dateString: string): string {
//   return new Date(dateString).toLocaleTimeString();
// }

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Fetch task details
  const { data: taskResponse, isLoading: taskLoading, refetch: refetchTask } = deliveryTsr.adminTasks.getById.useQuery({
    queryKey: ['task', taskId],
    queryData: { params: { taskId: taskId! } },
  });
  const task = taskResponse?.status === 200 ? taskResponse.body : null;

  // Fetch assigned driver details (if driver is assigned)
  const { data: driverResponse, isLoading: driverLoading } = deliveryTsr.adminDrivers.getById.useQuery({
    queryKey: ['driver', task?.driver?.id],
    queryData: { params: { driverId: task?.driver?.id ?? '' } },
    enabled: !!task?.driver?.id,
  });
  const assignedDriver = driverResponse?.status === 200 ? driverResponse.body : null;

  // Fetch timeline
  const { data: timelineResponse, isLoading: timelineLoading } = deliveryTsr.adminTasks.getTimeline.useQuery({
    queryKey: ['task-timeline', taskId],
    queryData: { params: { taskId: taskId! } },
  });
  const timeline = timelineResponse?.status === 200 ? timelineResponse.body : [];

  // Fetch all drivers for assignment (including offline drivers)
  const { data: driversResponse, isLoading: driversLoading } = deliveryTsr.adminDrivers.list.useQuery({
    queryKey: ['drivers-for-assign'],
    queryData: { query: { limit: 50 } },
  });
  const drivers = driversResponse?.status === 200 ? driversResponse.body : [];

  // Fetch teams for assignment
  const { data: teamsResponse, isLoading: teamsLoading } = deliveryTsr.adminTeams.list.useQuery({
    queryKey: ['teams-for-assign'],
  });
  const teams = teamsResponse?.status === 200 ? teamsResponse.body : [];

  // Fetch order details if task has an orderId
  const { data: orderResponse, isLoading: orderLoading } = deliveryTsr.adminOrders.getById.useQuery({
    queryKey: ['order', task?.orderId],
    queryData: { params: { orderId: task?.orderId ?? 0 } },
    enabled: !!task?.orderId,
  });
  const order = orderResponse?.status === 200 ? orderResponse.body : null;

  // Mutations
  const updateMutation = deliveryTsr.adminTasks.update.useMutation();
  const assignMutation = deliveryTsr.adminTasks.assign.useMutation();
  const unassignMutation = deliveryTsr.adminTasks.unassign.useMutation();
  const cancelMutation = deliveryTsr.adminTasks.cancel.useMutation();
  const deleteMutation = deliveryTsr.adminTasks.delete.useMutation();

  // Edit modal handlers
  const handleUpdateNotes = useCallback(async (notes: string) => {
    if (!taskId) return;

    try {
      const result = await updateMutation.mutateAsync({
        params: { taskId },
        body: { notes },
      });

      if (result.status === 200) {
        toast.success('Notes updated successfully');
        refetchTask();
      } else {
        const errorBody = result.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to update notes');
      }
    } catch (error) {
      console.error('Failed to update notes:', error);
      toast.error(getErrorMessage(error, 'Failed to update notes'));
    }
  }, [taskId, updateMutation, refetchTask]);

  const handleUpdateTeam = useCallback(async (teamId: string) => {
    if (!taskId) return;

    try {
      const result = await updateMutation.mutateAsync({
        params: { taskId },
        body: { teamId },
      });

      if (result.status === 200) {
        toast.success('Team updated successfully');
        refetchTask();
      } else {
        const errorBody = result.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to update team');
      }
    } catch (error) {
      console.error('Failed to update team:', error);
      toast.error(getErrorMessage(error, 'Failed to update team'));
    }
  }, [taskId, updateMutation, refetchTask]);

  const handleAssignDriver = useCallback(async (driverId: string) => {
    if (!taskId) return;

    try {
      const result = await assignMutation.mutateAsync({
        params: { taskId },
        body: { driverId },
      });

      if (result.status === 200) {
        toast.success('Driver assigned successfully');
        refetchTask();
      } else {
        const errorBody = result.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to assign driver');
      }
    } catch (error) {
      console.error('Failed to assign driver:', error);
      toast.error(getErrorMessage(error, 'Failed to assign driver'));
    }
  }, [taskId, assignMutation, refetchTask]);

  const handleUnassignDriver = useCallback(async () => {
    if (!taskId) return;

    try {
      const result = await unassignMutation.mutateAsync({
        params: { taskId },
        body: {},
      });

      if (result.status === 200) {
        toast.success('Driver unassigned successfully');
        refetchTask();
      } else {
        const errorBody = result.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to unassign driver');
      }
    } catch (error) {
      console.error('Failed to unassign driver:', error);
      toast.error(getErrorMessage(error, 'Failed to unassign driver'));
    }
  }, [taskId, unassignMutation, refetchTask]);

  const handleCancelTask = async () => {
    if (!taskId) return;

    try {
      const result = await cancelMutation.mutateAsync({
        params: { taskId },
        body: { reason: cancelReason || undefined },
      });

      if (result.status === 200) {
        toast.success('Task cancelled');
        setIsCancelDialogOpen(false);
        setCancelReason('');
        refetchTask();
      } else {
        const errorBody = result.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to cancel task');
      }
    } catch (error) {
      console.error('Failed to cancel task:', error);
      toast.error(getErrorMessage(error, 'Failed to cancel task'));
    }
  };

  const handleDeleteTask = async () => {
    if (!taskId) return;

    try {
      const result = await deleteMutation.mutateAsync({
        params: { taskId },
        body: {},
      });

      if (result.status === 204) {
        toast.success('Task deleted');
        setIsDeleteDialogOpen(false);
        navigate('/tasks');
      } else {
        const errorBody = result.body as unknown as { message?: string };
        toast.error(errorBody?.message || 'Failed to delete task');
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error(getErrorMessage(error, 'Failed to delete task'));
    }
  };

  if (taskLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="space-y-6">
        <Link to="/tasks" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Tasks
        </Link>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Truck className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="mb-2 text-lg font-medium">Task not found</h3>
            <p className="text-sm text-muted-foreground">
              The task you're looking for doesn't exist or has been removed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canEdit = ['pending', 'assigned'].includes(task.status);
  const canCancel = ['pending', 'assigned'].includes(task.status);
  const canDelete = ['pending', 'failed'].includes(task.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link to="/tasks" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Tasks
        </Link>
        <div className="flex gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => setIsEditModalOpen(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit Task
            </Button>
          )}
          {canCancel && (
            <Button variant="destructive" onClick={() => setIsCancelDialogOpen(true)}>
              <XCircle className="mr-2 h-4 w-4" />
              Cancel Task
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={() => setIsDeleteDialogOpen(true)}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Task
            </Button>
          )}
        </div>
      </div>

      {/* Task Info Card */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
              <div className="flex items-center gap-4">
                <h1 className="font-mono text-2xl font-bold">{task.trackingId}</h1>
                <TaskStatusBadge status={task.status} />
              </div>

              <div className="flex flex-col gap-4 lg:flex-row lg:justify-between">
                {/* Recipient */}
                <div className="space-y-1 lg:flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Recipient</p>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{task.recipientName}</span>
                  </div>
                  {task.recipientPhone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{task.recipientPhone}</span>
                    </div>
                  )}
                </div>

                {/* Address */}
                <div className="space-y-1 lg:flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Delivery Address</p>
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div>
                      <p>{task.address.number} {task.address.street}{task.address.apartment ? `, ${task.address.apartment}` : ''}</p>
                      <p>{task.address.city}, {task.address.state} {task.address.postalCode}</p>
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="space-y-1 lg:flex-1">
                  <p className="text-sm font-medium text-muted-foreground">Timestamps</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">Created: {formatDateTime(task.createdAt)}</span>
                  </div>
                  {task.estimatedArrival && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">ETA: {formatDateTime(task.estimatedArrival)}</span>
                    </div>
                  )}
                  {task.completedAt && (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Completed: {formatDateTime(task.completedAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {task.notes && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Notes</p>
                  <div className="flex items-start gap-2">
                    <FileText className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <p className="text-sm">{task.notes}</p>
                  </div>
                </div>
              )}

              {/* Failure Reason */}
              {task.failureReason && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-destructive">Failure Reason</p>
                  <p className="text-sm">{task.failureReason}</p>
                </div>
              )}
          </div>
        </CardContent>
      </Card>

      {/* Order Details Card */}
      {task.orderId && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Order Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {orderLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : order ? (
              <div className="space-y-4">
                {/* Order Summary */}
                <div className="flex flex-col gap-4 sm:flex-row sm:justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Order ID</p>
                    <p className="text-lg font-semibold">#{order.id}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Customer</p>
                    <p className="font-medium">{order.customerCompany || order.customerName || 'Unknown'}</p>
                    {order.customerCompany && order.customerName && (
                      <p className="text-sm text-muted-foreground">{order.customerName}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Total</p>
                    <p className="text-lg font-bold">${order.grossTotal}</p>
                    {order.currency && order.currency !== 'USD' && (
                      <p className="text-xs text-muted-foreground">{order.currency}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Status</p>
                    <p className="text-sm">{order.statusOrderName || `Status ${order.statusOrderId}`}</p>
                  </div>
                </div>

                {/* Order Products */}
                {order.orderProducts && order.orderProducts.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">
                      Products ({order.orderProducts.length})
                    </p>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>SKU</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Price</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {order.orderProducts.map((product) => (
                            <TableRow key={product.id}>
                              <TableCell className="font-mono text-sm">{product.productCode || '-'}</TableCell>
                              <TableCell>{product.productName || 'Unknown'}</TableCell>
                              <TableCell className="text-right">{product.quantity}</TableCell>
                              <TableCell className="text-right">${product.price}</TableCell>
                              <TableCell className="text-right font-medium">${product.totalAfterVat}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Comments */}
                {(order.commentsCustomer || order.commentsWholesaler) && (
                  <div className="space-y-2">
                    {order.commentsCustomer && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Customer Comments</p>
                        <p className="text-sm">{order.commentsCustomer}</p>
                      </div>
                    )}
                    {order.commentsWholesaler && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Wholesaler Comments</p>
                        <p className="text-sm">{order.commentsWholesaler}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-4 text-center">
                <ShoppingCart className="mb-2 h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  Order #{task.orderId} could not be loaded
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Driver Card */}
      {task.driver && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Assigned Driver
            </CardTitle>
          </CardHeader>
          <CardContent>
            {driverLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {assignedDriver?.photoUrl ? (
                    <img
                      src={assignedDriver.photoUrl}
                      alt={assignedDriver.name}
                      className="h-16 w-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                      <User className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Driver Info Grid */}
                <div className="flex-1 grid gap-4 sm:grid-cols-3">
                  {/* Name & Status */}
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Driver</p>
                    <p className="font-medium">{assignedDriver?.name || task.driver.name}</p>
                    {assignedDriver && (
                      <DriverStatusBadge status={assignedDriver.status} />
                    )}
                  </div>

                  {/* Contact */}
                  {assignedDriver?.phone && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">Contact</p>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{assignedDriver.phone}</span>
                      </div>
                    </div>
                  )}

                  {/* Vehicle */}
                  {assignedDriver?.vehicle?.description && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">Vehicle</p>
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-muted-foreground" />
                        <span>{assignedDriver.vehicle.description}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action */}
                <div className="flex-shrink-0">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/drivers/${task.driver.id}`}>View Driver</Link>
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Timeline Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          {timelineLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : timeline.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Clock className="mb-2 h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No timeline events yet</p>
            </div>
          ) : (
            <div className="relative space-y-4">
              {/* Timeline line */}
              <div className="absolute left-[17px] top-2 h-[calc(100%-16px)] w-0.5 bg-border" />

              {timeline.map((entry, index) => {
                const StatusIcon = STATUS_ICONS[entry.status] || AlertCircle;
                return (
                  <div key={index} className="relative flex gap-4 pl-10">
                    {/* Icon */}
                    <div className="absolute left-0 flex h-9 w-9 items-center justify-center rounded-full border bg-background">
                      <StatusIcon className="h-4 w-4" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <TaskStatusBadge status={entry.status} className="text-xs" />
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(entry.timestamp)}
                        </span>
                      </div>
                      {entry.message && (
                        <p className="text-sm text-muted-foreground">{entry.message}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Proof of Delivery Card */}
      {task.proofOfDelivery && (task.proofOfDelivery.signatureUrl || task.proofOfDelivery.photoUrls?.length || task.proofOfDelivery.notes) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Proof of Delivery
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Signature */}
            {task.proofOfDelivery.signatureUrl && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Signature</p>
                <div className="rounded-md border bg-muted/50 p-4">
                  <img
                    src={task.proofOfDelivery.signatureUrl}
                    alt="Delivery signature"
                    className="max-h-32 object-contain"
                  />
                </div>
              </div>
            )}

            {/* Photos */}
            {task.proofOfDelivery.photoUrls && task.proofOfDelivery.photoUrls.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Photos</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                  {task.proofOfDelivery.photoUrls.map((url, index) => (
                    <a
                      key={index}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative aspect-square overflow-hidden rounded-md border"
                    >
                      <img
                        src={url}
                        alt={`Delivery photo ${index + 1}`}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                        <ImageIcon className="h-6 w-6 text-white" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {task.proofOfDelivery.notes && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Delivery Notes</p>
                <p className="text-sm">{task.proofOfDelivery.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Task Edit Modal */}
      {task && (
        <TaskEditModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          task={task}
          teams={teams}
          teamsLoading={teamsLoading}
          drivers={drivers}
          driversLoading={driversLoading}
          onUpdateNotes={handleUpdateNotes}
          onUpdateTeam={handleUpdateTeam}
          onAssignDriver={handleAssignDriver}
          onUnassignDriver={handleUnassignDriver}
          isUpdating={updateMutation.isPending}
          isAssigning={assignMutation.isPending}
          isUnassigning={unassignMutation.isPending}
        />
      )}

      {/* Cancel Task Dialog */}
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Task</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this delivery task? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="cancelReason" className="text-sm font-medium">
                Reason (optional)
              </label>
              <Textarea
                id="cancelReason"
                placeholder="Enter cancellation reason..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCancelDialogOpen(false)}>
              Keep Task
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelTask}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cancel Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Task Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this task? This will remove it from both Onfleet and the local database. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Keep Task
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTask}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
